import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

export type SignupFormData = {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  password: string;
  phoneNumber?: string;
};

export type Props = {
  /** Form instance from useForm */
  form: UseFormReturn<SignupFormData>;
  /** Handler for form submission */
  onSignup: () => void;
  /** Handler for Google signup */
  onSignupWithGoogle: () => void;
  /** Whether to hide the login link */
  hideLoginLink?: boolean;
  /** Render the form fields */
  renderForm: (props: { form: UseFormReturn<SignupFormData> }) => ReactNode;
  /** Render the action buttons */
  renderActions: (props: {
    isSubmitting: boolean;
    isValid: boolean;
    onSignup: () => void;
    onSignupWithGoogle: () => void;
  }) => ReactNode;
  /** Render additional content (links, etc.) */
  renderAdditionalContent?: (props: { hideLoginLink?: boolean }) => ReactNode;
  /** Container className */
  className?: string;
  /** Gap between sections (in pixels) */
  gap?: number;
};

/**
 * Base signup form component - defines structure with render props.
 * Each app implements the render props with their own styling.
 */
export function SignupFormBase({
  form,
  onSignup,
  onSignupWithGoogle,
  hideLoginLink,
  renderForm,
  renderActions,
  renderAdditionalContent,
  className,
  gap = 16,
}: Props) {
  const { isSubmitting, isValid } = form.formState;

  return (
    <div
      className={className}
      data-testid="signup-form"
      style={{ display: "flex", flexDirection: "column", gap: `${gap}px` }}
    >
      {renderForm({ form })}
      {renderActions({ isSubmitting, isValid, onSignup, onSignupWithGoogle })}
      {renderAdditionalContent?.({ hideLoginLink })}
    </div>
  );
}

export default SignupFormBase;
